
import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ChevronLeft, AlertTriangle, CheckCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import { useToast } from "@/hooks/use-toast";
import { restoreWebsite } from "@/services/wordpressService";

export default function Restore() {
  const { websiteId, backupId } = useParams<{ websiteId: string, backupId: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Get mock website data - in a real app, you'd fetch this from an API
  const website = {
    id: websiteId || "",
    title: "My WordPress Blog",
    url: "myblog.example.com",
    platform: "wordpress" as const,
    lastBackup: "Today, 08:30 AM",
    nextBackup: "Tomorrow, 08:30 AM",
    storageUsed: 35,
    status: "active" as const,
  };

  // Format date for display - in a real app, you'd fetch this data
  const backupDate = "May 12, 2024, 10:30 AM";
  const backupSize = "45.2 MB";

  const handleRestore = async () => {
    if (!websiteId || !backupId) {
      toast({
        title: "Restore failed",
        description: "Missing website or backup information.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Call the WordPress restore API
      const success = await restoreWebsite(website, backupId);
      
      if (success) {
        toast({
          title: "Restore started",
          description: "Your website is being restored. This may take several minutes.",
        });
        navigate(`/dashboard`);
      } else {
        throw new Error("Restore failed");
      }
    } catch (error) {
      toast({
        title: "Restore failed",
        description: "There was an error restoring your website. Please try again.",
        variant: "destructive",
      });
      console.error("Restore error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isLoggedIn={true} />
      
      <div className="container mx-auto py-10 px-4 max-w-6xl">
        <Button variant="outline" asChild className="mb-6">
          <Link to={`/backup-history/${websiteId}`}>
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back to Backup History
          </Link>
        </Button>
        
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle>Restore Website</CardTitle>
            <CardDescription>
              You are about to restore {website.title} from a backup created on {backupDate}.
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-1">Website</h3>
              <p className="text-sm">{website.title} ({website.url})</p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium mb-1">Backup Details</h3>
              <p className="text-sm">Created on {backupDate}</p>
              <p className="text-sm">Size: {backupSize}</p>
            </div>
            
            <Alert className="border-yellow-300 bg-yellow-50 text-yellow-800">
              <AlertTriangle className="h-4 w-4 text-yellow-800" />
              <AlertTitle>Warning</AlertTitle>
              <AlertDescription>
                Restoring will replace all current data with the data from this backup.
                This action cannot be undone.
              </AlertDescription>
            </Alert>
          </CardContent>
          
          <CardFooter className="flex justify-end gap-3">
            <Button variant="outline" asChild>
              <Link to={`/backup-history/${websiteId}`}>Cancel</Link>
            </Button>
            <Button 
              onClick={handleRestore}
              disabled={isLoading}
              className="flex gap-2"
            >
              {isLoading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                  Restoring...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Restore Website
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
